import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { capability, consultingBundle, dfyBundle } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const donorMgmt = capability('donor-management')!
const concierge = dfyBundle('concierge')
const intensive = consultingBundle('strategy-intensive')

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'Platform fees eat the gift',
    description:
      'Most giving tools take 3 to 4 percent of every donation, on top of card fees. On a year of giving that is a real number your mission never sees.',
  },
  {
    problem: 'Receipts are written by hand',
    description:
      'Every January someone spends a week assembling tax letters from bank statements and memory. Donors wait, and some ask twice.',
  },
  {
    problem: 'Nobody knows who lapsed',
    description:
      'The donor who gave three years running and then stopped is your most recoverable gift, and the one no spreadsheet ever surfaces.',
  },
  {
    problem: 'Donors and audience live apart',
    description: 'Your giving tool does not know who bought tickets or opens your emails. Every appeal goes out blind.',
  },
]

const FAQS = [
  {
    q: 'Is accepting donations really free?',
    a: 'Yes, forever. A Donate button with money going straight to your own Stripe account, 0% platform fee. You only ever pay Stripe card processing. Nobody should pay software a cut to accept a gift.',
  },
  {
    q: `What does the $${donorMgmt.priceMonthly}/mo buy, then?`,
    a: 'The management layer: recurring giving, automated tax receipts, campaign tracking, and donor records (who gave what and when, segments, lapsed-donor lists). You pay to stop losing track of donors, not to accept money.',
  },
  {
    q: 'Who runs it?',
    a: `Today our team sets up and runs donor management for you; self-serve controls are rolling out. If you want fundraising itself run end to end, that lives in Concierge (${concierge.price}), because good fundraising is strategy plus execution, not a task queue. The ${intensive.name} (${intensive.price}) is the usual starting point.`,
  },
  {
    q: 'Can we bring our donor history?',
    a: 'Yes. Simple imports are part of setup; large donor-history migrations are scoped separately. Your data is always exportable, free, on the way out.',
  },
]

export default function DonorManagementPage() {
  const breadcrumbs = generateBreadcrumbs('/services/donor-management', breadcrumbLabels)

  useSEO({
    title: 'Donor Management for Music Organizations',
    // Price interpolates from pricing.ts; it was a literal $100 here.
    description: `Accept donations free (0% platform fee). Add recurring giving, automated tax receipts, and donor records for $${donorMgmt.priceMonthly}/mo, or have Fugue Lab run your fundraising.`,
    url: '/services/donor-management',
    canonical: '/services/donor-management',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Donor Management for Music Organizations',
      description:
        'Free donation acceptance with 0% platform fee, plus donor management for music organizations, with fundraising run end to end in Concierge engagements.',
      url: '/services/donor-management',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Keep the whole gift. Keep the donor.</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Accepting donations on Fugue Lab is free, with a 0% platform fee: the money goes straight to your own Stripe
          account. Donor management is the layer that makes giving grow.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Recurring gifts, automated tax receipts, campaigns, and a donor record that actually knows your audience, for
          ${donorMgmt.priceMonthly} a month. Or have us run fundraising end to end.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/pricing">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          What giving tools get wrong
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Unified CRM differentiator */}
      <section aria-labelledby="one-record" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="one-record" className="text-fluid-h3 md:col-span-5">
          Your donors already sit in your audience
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">
            Because giving runs on the same system as your email and ticketing, every donor is one record with the rest
            of their history: the concerts they attended, the newsletters they open. Ask for the right gift from the
            right person, and see who loves you but has never been asked.
          </p>
          <p className="max-w-[60ch]">
            A standalone giving tool cannot see any of that. Pair this page with{' '}
            <Link to="/services/email-marketing" className={INLINE_LINK}>
              Email Marketing
            </Link>{' '}
            and the appeal writes its own segment.
          </p>
        </div>
      </section>

      {/* Pricing. Three genuinely comparable options, so they stay side by side
          on a plain ground; the recommended tier is set apart by a raised
          ground, not a tint, glow or hover lift. */}
      <section aria-labelledby="pricing" className="space-y-12">
        <h2 id="pricing" className="text-fluid-h3">
          Free to accept. Priced to manage.
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          <div className="bg-surface flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">Accept Donations</h3>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl leading-none">Free</span>
              <span className="text-muted-foreground">forever</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Donate button on your site',
                'Money straight to your own Stripe',
                '0% platform fee',
                'Only Stripe card processing applies',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/platform/features/donations" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                How It Works
              </Button>
            </Link>
          </div>
          <div className="bg-card flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">{donorMgmt.name}</h3>
            <p className="text-muted-foreground mt-1 text-sm">The management layer</p>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-5xl leading-none">${donorMgmt.priceMonthly}</span>
              <span className="text-muted-foreground">/month</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Recurring giving',
                'Automated tax receipts',
                'Campaign tracking',
                'Donor records, segments, lapsed lists',
                'Set up and run for you by our team today',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button className="w-full cursor-pointer">Get Started</Button>
            </Link>
          </div>
          <div className="bg-surface flex flex-col rounded-md p-8">
            <h3 className="text-fluid-h4">Fundraising, run for you</h3>
            <p className="text-muted-foreground mt-1 text-sm">Done for you</p>
            <p className="mt-6 space-y-1">
              <span className="font-display block text-3xl leading-tight">{concierge.name}</span>
              <span className="text-muted-foreground block">{concierge.price}</span>
            </p>
            <ul className="text-muted-foreground mt-6 mb-8 flex-1 space-y-2 leading-relaxed">
              {[
                'Campaign strategy and appeal copy',
                'Segments and lapsed-donor outreach',
                'Receipting oversight',
                'Part of everything-done-for-you, because fundraising is strategy plus execution',
              ].map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/services/done-for-you#concierge" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">Pay annually on any plan and get 2 months free.</p>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
